/**
 * useAircraftVisitHistory - Fetch past transient stays for an aircraft
 *
 * Returns the last N completed transient stays (checked_out status).
 */

import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

export interface VisitHistoryEntry {
  id: string
  bookingNumber: string
  checkIn: string
  checkOut: string
  nightsCount: number | null
  totalAmount: number | null
  unitCode: string | null
  areaName: string | null
}

export function useAircraftVisitHistory(aircraftId: string | null) {
  return useQuery({
    queryKey: ['aircraft-visit-history', aircraftId],
    queryFn: async () => {
      if (!aircraftId) return []

      const { data, error } = await supabase
        .from('transient_stays')
        .select(`
          id,
          booking_number,
          check_in,
          actual_check_out,
          expected_check_out,
          nights_count,
          total_amount,
          units!unit_id (unit_code),
          areas!area_id (name)
        `)
        .eq('aircraft_id', aircraftId)
        .eq('status', 'checked_out')
        .is('deleted_at', null)
        .order('actual_check_out', { ascending: false, nullsFirst: false })
        .limit(5)

      if (error) throw error
      if (!data) return []

      return data.map((row: any): VisitHistoryEntry => ({
        id: row.id,
        bookingNumber: row.booking_number,
        checkIn: row.check_in,
        checkOut: row.actual_check_out || row.expected_check_out,
        nightsCount: row.nights_count,
        totalAmount: row.total_amount,
        unitCode: row.units?.unit_code ?? null,
        areaName: row.areas?.name ?? null,
      }))
    },
    enabled: !!aircraftId,
    staleTime: 60_000,
  })
}
